<script setup lang="ts">
// Always-visible checkbox group. Same value shape as multiselect (an
// array of selected option values), but every option is on screen at
// once — pick this when the option count is small enough that a
// popover would be overkill.

import { computed } from "vue";

import type { CheckboxOpts, SelectOption } from "../sbi/protocol";
import { usePrintMode } from "../sbi/print-mode.ts";
import PrintStaticControl from "./PrintStaticControl.vue";
import InfoTip from "./InfoTip.vue";

const props = defineProps<{
  id: string;
  value: ReadonlyArray<string | number>;
  options: ReadonlyArray<SelectOption<string | number>>;
  opts?: CheckboxOpts;
}>();

const emit = defineEmits<{
  (
    e: "change-target",
    id: string,
    value: Array<string | number>,
  ): void;
}>();

const selected = computed(() => new Set(props.value.map((v) => String(v))));
const layout = computed(() => props.opts?.layout ?? "horizontal");

const isPrint = usePrintMode();

// Same display shape as Multiselect — comma-joined labels for selected
// options. Empty selection renders "(none)" so the reader can tell
// the filter was unset, not omitted.
const printValue = computed(() => {
  if (props.value.length === 0) return "(none)";
  return props.value
    .map((v) => {
      const match = props.options.find(
        (o) => String(o.value) === String(v),
      );
      return match?.label ?? String(v);
    })
    .join(", ");
});

function toggle(option: SelectOption<string | number>, checked: boolean): void {
  const next = new Set(props.value);
  if (checked) next.add(option.value);
  else next.delete(option.value);
  // Preserve original option order when serialising the new array so
  // downstream SQL sees a stable shape regardless of click sequence.
  const ordered = props.options
    .filter((o) => next.has(o.value))
    .map((o) => o.value);
  emit("change-target", props.id, ordered);
}
</script>

<template>
  <PrintStaticControl
    v-if="isPrint()"
    :label="opts?.label"
    :value="printValue"
  />
  <fieldset v-else class="checkbox-group">
    <legend v-if="opts?.label || opts?.description" class="checkbox-label">
      <template v-if="opts?.label">{{ opts.label }}</template>
      <InfoTip v-if="opts?.description" :text="opts.description" :label="opts?.label" />
    </legend>
    <div class="options" :class="layout">
      <!--
        Empty-state branch — `options` populated dynamically from a
        query that returned zero rows. A short "No options" line
        reads as intentional empty state, distinct from a partially-
        rendered control or a layout bug.
      -->
      <span v-if="options.length === 0" class="empty">No options</span>
      <label
        v-for="o in options"
        v-else
        :key="String(o.value)"
        class="option"
      >
        <input
          type="checkbox"
          :value="String(o.value)"
          :checked="selected.has(String(o.value))"
          @change="(ev) => toggle(o, (ev.target as HTMLInputElement).checked)"
        />
        <span>{{ o.label }}</span>
      </label>
    </div>
  </fieldset>
</template>

<style scoped>
/* Never wider than the slot it sits in; full width when that slot is
   narrow (filter bar < 480px, a narrow column cell). */
.checkbox-group {
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
  border: 0;
  margin: 0;
  padding: 0;
  font-size: 13px;
}
@container (max-width: 479px) {
  .checkbox-group {
    display: flex;
    width: 100%;
  }
}
.checkbox-label {
  color: var(--color-text-muted);
  font-size: 12px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.options {
  display: flex;
  gap: 12px;
}
.options.horizontal {
  flex-direction: row;
  flex-wrap: wrap;
}
.options.vertical {
  flex-direction: column;
  gap: 6px;
}
.option {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  color: var(--card-ink);
  min-width: 0;
  overflow-wrap: anywhere;
}
.options {
  max-width: 100%;
}
.option input[type="checkbox"] {
  accent-color: var(--primary);
}
.empty {
  color: var(--color-text-muted);
  font-style: italic;
  font-size: 12px;
}
/* Touch: 16px text (iOS zooms the page when a smaller input takes
   focus) and 40–44px targets. Desktop keeps the 13px density. */
@media (pointer: coarse) {
  .option {
    min-height: 40px;
  }
  .option input[type="checkbox"] {
    width: 20px;
    height: 20px;
  }
}
</style>
